<template>
  <input
    ref="autocompleteInput"
    v-model="streetAddress"
    type="text"
    class="form-control"
    :required="required"
    placeholder="Enter an address or location name"
    @focusin="toggleAutocompleteInputMeme"
    @focusout="onFocusOut"
    @keydown.enter="onEnterKey"
  >
  <div
    v-if="isShowCache"
    ref="autocompleteInputMeme"
    class="address_autocomplete_meme hidden"
  >
    <button
      v-for="place of googlePlaceCache"
      :key="place.place_id"
      type="button"
      @click="onPlaceChanged(place)"
    >
      {{ parseAddressComponentsToAddress(place.address_components).streetAddress }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, WritableComputedRef, watch, nextTick, PropType, onMounted } from 'vue';

import { useAddress } from '@/use/address';
import { googleAutocompleteOptionFields } from '@/constants/address';
import { CustomLocationType } from '@/models/address';
import { googlePlaceCache, updateGooglePlaceCache } from '@/services/address/googlePlaceCache';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },

  anchorBound: {
    type: Object as PropType<google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined>,
    default: () => undefined,
  },

  addressType: {
    type: String,
    validator: (prop: string) => prop === 'Street Address' || prop === 'Full Address',
    default: 'Street Address',
  },

  required: {
    type: Boolean,
    default: true,
  },

  autoSelectFirst: {
    type: Boolean,
    default: false,
  },
});

const isShowCache = computed(() => {
  return isStreetAddressInput.value && googlePlaceCache.value.length && !streetAddress.value;
})

const emit = defineEmits(['place-changed', 'update:modelValue']);

const { parseAddressComponentsToAddress } = useAddress();

const autocompleteInput: Ref<HTMLInputElement | null> = ref(null);
const autocompleteInputMeme: Ref<HTMLInputElement | null> = ref(null);

const streetAddress: WritableComputedRef<string> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isStreetAddressInput = computed(() => props.addressType === 'Street Address');

const autocomplete: Ref<google.maps.places.Autocomplete | null> = ref(null);

const initComponent = () => {
  if (!autocompleteInput.value) {
    return;
  }

  const options = {
    fields: googleAutocompleteOptionFields,
    strictBounds: false,
    bounds: props.anchorBound,
  } as google.maps.places.AutocompleteOptions;

  // reference: https://developers.google.com/maps/documentation/javascript/places-autocomplete
  autocomplete.value = new google.maps.places.Autocomplete(autocompleteInput.value, options);

  autocomplete.value.addListener('place_changed', () => {
    if (!autocomplete.value) {
      return emit('place-changed', null, null);
    }

    placeWasSelected.value = true;
    const place = autocomplete.value.getPlace() as google.maps.places.PlaceResult;
    onPlaceChanged(place);
  }); // triggered when an address from Google's dropdown of addresses is selected
};

const onPlaceChanged = (place: google.maps.places.PlaceResult) => {
  if (!place?.place_id) {
    return emit('place-changed', null, place);
  }

  const address = parseAddressComponentsToAddress(place.address_components) as CustomLocationType;
  address.streetAddress2 = '';
  address.latitude = place?.geometry?.location?.lat();
  address.longitude = place?.geometry?.location?.lng();
  address.googlePlaceId = place?.place_id;
  address.googlePlaceTypes = place.types;
  address.name = place.name;

  if (isStreetAddressInput.value) {
    /*
      if streetAddress is same,
      the auto complete component is not reactive,
      so need to update the value manually
    */

    updateGooglePlaceCache(place);

    if (autocompleteInput.value) {
      autocompleteInput.value.value = address.streetAddress || '';
    }
  }

  emit('place-changed', address, place);
};

watch(
  () => props.anchorBound,
  (value: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined) => {
    // reference: https://developers.google.com/maps/documentation/javascript/places-autocomplete#set-search-area
    if (!autocomplete.value) {
      return;
    }

    if (value) {
      autocomplete.value.setBounds(value);
    } else {
      autocomplete.value.setBounds(undefined);
    }
  },
);

const toggleAutocompleteInputMeme = (ev: FocusEvent) => {
  if (ev.type === 'focusin') {
    autocompleteInputMeme.value?.classList.remove('hidden');

    // relatedTarget is the element that gets the focus
    // if that element is inside the meme then don't hide the meme or the click wouldn't work
  } else if (!autocompleteInputMeme.value?.contains(ev.relatedTarget as Node)) {
    autocompleteInputMeme.value?.classList.add('hidden');
  }
}

const placeWasSelected = ref(false);

watch(() => props.modelValue, () => {
  // Reset selection flag when input changes (user is typing)
  placeWasSelected.value = false;
});

const selectFirstPrediction = async () => {
  if (!props.autoSelectFirst || placeWasSelected.value || !streetAddress.value?.trim()) {
    return;
  }

  try {
    const service = new google.maps.places.AutocompleteService();
    const request: google.maps.places.AutocompletionRequest = {
      input: streetAddress.value,
      bounds: props.anchorBound as google.maps.LatLngBounds | undefined,
    };

    service.getPlacePredictions(request, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions?.length) {
        return;
      }

      const firstPrediction = predictions[0];
      const placesService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );

      placesService.getDetails(
        {
          placeId: firstPrediction.place_id,
          fields: googleAutocompleteOptionFields,
        },
        (place, detailStatus) => {
          if (detailStatus === google.maps.places.PlacesServiceStatus.OK && place) {
            placeWasSelected.value = true;
            onPlaceChanged(place);

            if (autocompleteInput.value) {
              autocompleteInput.value.value = place.formatted_address || streetAddress.value;
              emit('update:modelValue', place.formatted_address || streetAddress.value);
            }
          }
        },
      );
    });
  } catch {
    // Silently fail - user can still manually select
  }
};

const onFocusOut = (ev: FocusEvent) => {
  toggleAutocompleteInputMeme(ev);
  selectFirstPrediction();
};

const onEnterKey = (ev: KeyboardEvent) => {
  if (props.autoSelectFirst) {
    ev.preventDefault();
    selectFirstPrediction();
  }
};

onMounted(async () => {
  await nextTick();
  initComponent();
});
</script>

<style lang="scss">
.address_autocomplete_meme {
  position: absolute;
  z-index: 99;
  padding: 0;
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 4px;
  color: #66615b;
  box-shadow: 0px 5px 15px -5px #000000;
  width: inherit;

  &.hidden {
    display: none;
  }

  button {
    width: 100%;
    display: block;
    border: none;
    background-color: inherit;
    padding: 0.5em 1.1em;
    text-align: unset;
    font-weight: bold;

    &:hover {
      background-color: #f1f1f1;
    }

    + button {
      border-top: 1px solid #dddddd;
    }
  }
}
</style>
